import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useUIStore } from "../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { UIState } from "../types/ui-types";
import { fetchSynkkaSearchProductData } from "../api/searchProduct.api";
import { fetchFullProductData } from "../api/fullProductData.api";
import { fetchFullModelData } from "../api/fullModelData.api";

interface UseSynkkaEanSearchProps {
  ean: string;
  enabled?: boolean;
}

const useSynkkaEanSearch = ({
  ean,
  enabled = false,
}: UseSynkkaEanSearchProps) => {
  const [bearerToken] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken]),
  );
  const [searchEan, setSearchEan] = useState<string>(ean);

  useEffect(() => {
    setSearchEan(ean);
  }, [ean]);

  const {
    data: synkkaData,
    error: synkkaError,
    isLoading: isLoadingSynkka,
    refetch,
  } = useQuery({
    queryKey: ["synkkaSearch", searchEan],
    queryFn: async () => {
      if (!searchEan) return { data: [] };
      const result = await fetchSynkkaSearchProductData(bearerToken as string, [
        searchEan,
      ]);
      return result;
    },
    enabled: !!searchEan && enabled,
  });

  const fetchFullProduct = async (eanArray: string[]) => {
    const result = await fetchFullProductMutation.mutateAsync({
      bearerToken: bearerToken as string,
      searchStrings: eanArray,
    });
    return result.data;
  };

  const fetchFullProductMutation = useMutation({
    mutationFn: async ({
      bearerToken,
      searchStrings,
    }: {
      bearerToken: string;
      searchStrings: string[];
    }) => {
      const result = await fetchFullProductData(bearerToken, searchStrings);
      return result;
    },
    onError: (error) => {
      console.error("Failed to fetch full product data:", error);
    },
  });

  const fetchFullModel = async (eanArray: string[]) => {
    const result = await fetchFullModelMutation.mutateAsync({
      bearerToken: bearerToken as string,
      searchStrings: eanArray,
    });
    return result.data;
  };

  const fetchFullModelMutation = useMutation({
    mutationFn: async ({
      bearerToken,
      searchStrings,
    }: {
      bearerToken: string;
      searchStrings: string[];
    }) => {
      const result = await fetchFullModelData(bearerToken, searchStrings);
      return result;
    },
    onError: (error) => {
      console.error("Failed to fetch full model data:", error);
    },
  });

  const isLoadingFullProduct = fetchFullProductMutation.isPending;
  const isLoadingFullModel = fetchFullModelMutation.isPending;

  return {
    synkkaProducts: synkkaData?.data || [],
    synkkaError,
    isLoadingSynkka,
    setSearchEan,
    refetch,
    fetchFullProduct,
    fetchFullModel,
    isLoadingFullProduct,
    isLoadingFullModel,
  };
};

export default useSynkkaEanSearch;
